import React, { useState, useEffect } from 'react';
import {
  getHeroContent, HeroImage, StoreLogo, SearchBar, CartBtn, TabContent, StoreFooter, FaHeart, getTabLabel, InlineEditableText, MdFilterList
} from './ThemeLayoutHelpers';
import { MdMenu, MdClose, MdArrowForward, MdEnergySavingsLeaf, MdOutlineYard, MdSpa, MdYard } from 'react-icons/md';

export default function FloraNectarLayout({ p }) {
  const T = p.storeTheme || {};
  const primary = T.primaryColor || "#4a5d4e"; // Sage green
  const secondary = T.secondaryColor || "#889e81"; // Light leaf green
  const text = p.storefrontText || {};
  
  const [activePage, setActivePage] = useState("home");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeProcessStep, setActiveProcessStep] = useState(1);

  // Track scroll position
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const copy = getHeroContent(p, {
    eyebrow: "Nourished by the Earth",
    title: "Botanical Elixirs for Organic Skin.",
    subtitle: "Pamper your skin barrier with cold-pressed floral extracts and nectar compounds engineered to protect, heal, and brighten.",
    primaryCta: "Shop Botanicals",
    secondaryCta: "Our Process",
  });

  const navigateTo = (page, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setActivePage(page);
    setMobileMenuOpen(false);
    if (page === "home") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else if (page === "shop") {
      p.setActiveTab("products");
      setTimeout(() => {
        const el = document.getElementById("catalog-section");
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#faf8f5] text-[#2c3531] antialiased" style={{ fontFamily: T.fontFamily || "Inter, sans-serif" }}>
      {/* Botanical banner */}
      <div className="w-full bg-[#f0ede6] border-b border-[#e1dcd1] py-2 px-4 text-center text-xs font-medium tracking-wide text-[#5c6b5e]">
        <span>🍃 GREEN BEAUTY: 100% biodegradable ingredients. Safe for your skin & the planet. </span>
        <button onClick={(e) => navigateTo("shop", e)} className="underline hover:text-black ml-1 inline-flex items-center gap-0.5 font-bold">
          Explore catalog <MdArrowForward className="text-[10px]" />
        </button>
      </div>

      {/* Nav */}
      <nav className={`sticky top-0 z-40 transition-all duration-300 ${
        isScrolled 
          ? "bg-[#faf8f5]/90 backdrop-blur-md border-b border-[#e1dcd1] shadow-sm" 
          : "bg-transparent border-b border-transparent"
      }`}>
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 cursor-pointer" onClick={(e) => navigateTo("home", e)}>
            <StoreLogo storeInfo={p.storeInfo} border="border border-[#e1dcd1]" size="h-9 w-9" />
            <div>
              <p className="text-base font-bold font-serif tracking-tight text-[#2c3531]">{p.storeInfo?.businessName}</p>
              <p className="text-[9px] text-[#4a5d4e] tracking-widest font-bold uppercase mt-0.5">{p.storeInfo?.businessCategory || "Organic Apothecary"}</p>
            </div>
          </div>

          {/* Links */}
          <div className="hidden md:flex items-center gap-8">
            {[
              ["home", "Home"],
              ["shop", "Shop Botanicals"],
              ["details", "Our Story"],
            ].map(([page, label]) => (
              <button
                key={page}
                onClick={(e) => page === "details" ? (p.setActiveTab("details"), navigateTo("about", e)) : navigateTo(page, e)}
                className={`text-xs font-semibold tracking-wider uppercase transition-colors hover:text-[#4a5d4e] ${
                  activePage === page 
                    ? "text-[#4a5d4e] font-bold" 
                    : "text-gray-500"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Controls */}
          <div className="flex items-center gap-3.5">
            <SearchBar searchQuery={p.searchQuery} setSearchQuery={p.setSearchQuery} theme={T} className="hidden md:block w-48" />
            
            <button onClick={p.handleGoToWishlist} className="relative p-2 rounded-full hover:bg-gray-100 text-[#2c3531] transition-colors" title="Wishlist">
              <FaHeart className="text-base text-[#4a5d4e]" />
              {p.wishlistCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 h-4 w-4 text-[9px] font-bold rounded-full text-white flex items-center justify-center bg-[#4a5d4e] shadow-sm">
                  {p.wishlistCount}
                </span>
              )}
            </button>

            <CartBtn p={p} />
            {p.userMenuElement}

            <button 
              className="md:hidden p-2 rounded-full hover:bg-gray-100 text-[#2c3531]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <MdClose size={20} /> : <MdMenu size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 bg-[#faf8f5] border-b border-[#e1dcd1] shadow-xl p-6 flex flex-col gap-4 animate-fadeIn md:hidden">
            <SearchBar searchQuery={p.searchQuery} setSearchQuery={p.setSearchQuery} theme={T} className="w-full mb-2" />
            {[
              ["home", "Home"],
              ["shop", "Shop Botanicals"],
              ["details", "About Us"],
            ].map(([page, label]) => (
              <button
                key={page}
                onClick={(e) => page === "details" ? (p.setActiveTab("details"), navigateTo("about", e)) : navigateTo(page, e)}
                className={`py-2 text-left text-xs font-bold tracking-wider uppercase border-b border-gray-100 hover:text-[#4a5d4e] ${
                  activePage === page ? "text-[#4a5d4e]" : "text-gray-500"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </nav>

      <main className="flex-grow">
        {activePage === "home" && (
          <>
            {/* Splitscreen botanical hero */}
            <section className="max-w-6xl mx-auto px-6 pt-6 pb-12">
              <div className="relative rounded-3xl overflow-hidden bg-white border border-[#e1dcd1] shadow-sm p-8 md:p-14 flex flex-col md:flex-row items-center gap-10 min-h-[460px]">
                {/* Left content */}
                <div className="flex-1 text-left relative z-10">
                  <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-[#4a5d4e] mb-3">
                    <MdSpa className="text-sm text-[#4a5d4e]" />
                    <InlineEditableText p={p} field="heroEyebrow" value={copy.eyebrow}>{copy.eyebrow}</InlineEditableText>
                  </span>
                  <h1 className="text-4xl md:text-5xl font-bold font-serif text-[#2c3531] leading-tight mb-4 tracking-tight">
                    <InlineEditableText p={p} field="heroTitle" value={copy.title}>{copy.title}</InlineEditableText>
                  </h1>
                  <p className="text-gray-500 text-sm leading-relaxed mb-8 max-w-lg">
                    <InlineEditableText p={p} field="heroSubtitle" value={copy.subtitle}>{copy.subtitle}</InlineEditableText>
                  </p>
                  
                  <div className="flex flex-wrap gap-4">
                    <button 
                      onClick={() => navigateTo("shop")}
                      className="px-8 py-3 rounded-md text-xs font-bold uppercase tracking-wider text-white shadow-md active:scale-98 transition-all"
                      style={{ backgroundColor: primary }}
                    >
                      <InlineEditableText p={p} field="primaryCtaText" value={copy.primaryCta}>{copy.primaryCta}</InlineEditableText>
                    </button>
                    <button 
                      onClick={() => { p.setActiveTab("details"); navigateTo("about"); }}
                      className="px-8 py-3 rounded-md text-xs font-bold uppercase tracking-wider text-[#2c3531] border border-[#e1dcd1] bg-transparent hover:bg-gray-50 transition-colors"
                    >
                      <InlineEditableText p={p} field="secondaryCtaText" value={copy.secondaryCta}>{copy.secondaryCta}</InlineEditableText>
                    </button>
                  </div>
                </div>

                {/* Right image */}
                <div className="flex-1 w-full relative z-10 self-stretch rounded-2xl overflow-hidden border border-[#e1dcd1]/50 shadow-inner bg-[#f6f3eb]">
                  <HeroImage p={p} themeId="floranectar" className="h-[280px] md:h-full">
                    <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-white/20 to-transparent pointer-events-none" />
                  </HeroImage>
                </div>
              </div>
            </section>

            {/* Stepper process: Seed to serum */}
            <section className="max-w-6xl mx-auto px-6 py-12">
              <div className="bg-[#f5f2eb] border border-[#e1dcd1] rounded-3xl p-8 md:p-12 shadow-sm text-left">
                <div className="max-w-xl mb-10">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-[#4a5d4e]">OUR SACRED CHAIN</span>
                  <h2 className="text-3xl font-bold font-serif text-[#2c3531] mt-1">From seed to serum.</h2>
                  <p className="text-gray-500 text-xs mt-2">Every drops of nectar follows a strict organic extraction process to retain native plant bio-activity.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {[
                    { step: 1, title: "Sourcing & Harvest", icon: <MdOutlineYard />, desc: "We wild-harvest botanicals at their peak vitality from certified biodynamic farms." },
                    { step: 2, title: "Cold Extraction", icon: <MdEnergySavingsLeaf />, desc: "Zero chemical solvents. Cold-pressed processing maintains native molecular integrity." },
                    { step: 3, title: "Formulation Shield", icon: <MdYard />, desc: "Hand-blended in small micro-batches with high-performance botanical carriers." }
                  ].map((s) => (
                    <div 
                      key={s.step} 
                      className={`p-6 rounded-2xl border transition-all duration-300 ${
                        activeProcessStep === s.step 
                          ? "bg-white border-[#4a5d4e] shadow-md scale-102" 
                          : "bg-transparent border-[#e1dcd1]/60 cursor-pointer"
                      }`}
                      onClick={() => setActiveProcessStep(s.step)}
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className={`p-2.5 rounded-xl border ${activeProcessStep === s.step ? "bg-[#4a5d4e] text-white border-[#4a5d4e]" : "bg-white text-gray-500 border-[#e1dcd1]"}`}>
                          {s.icon}
                        </div>
                        <span className="text-xs font-bold text-gray-400">STEP 0{s.step}</span>
                      </div>
                      <h4 className="text-base font-bold text-[#2c3531]">{s.title}</h4>
                      <p className="text-xs text-gray-500 mt-2 leading-relaxed">{s.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Value badges */}
            <section className="bg-white border-y border-[#e1dcd1]/40 py-10 px-6">
              <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { title: "Wild Botanicals", icon: <MdEnergySavingsLeaf className="text-2xl text-[#4a5d4e]" />, desc: "Unrefined raw flora bases" },
                  { title: "No Fillers", icon: <MdSpa className="text-2xl text-[#4a5d4e]" />, desc: "Active ingredients prioritize" },
                  { title: "Biodegradable", icon: <MdOutlineYard className="text-2xl text-[#4a5d4e]" />, desc: "Zero toxic synthetic residue" },
                  { title: "Micro-batched", icon: <MdYard className="text-2xl text-[#4a5d4e]" />, desc: "Freshly blended formulation lock" }
                ].map((b, idx) => (
                  <div key={idx} className="flex flex-col items-center text-center p-4">
                    <div className="p-3 rounded-xl bg-[#faf8f5] mb-3 border border-[#e1dcd1]">{b.icon}</div>
                    <p className="text-xs font-bold text-[#2c3531]">{b.title}</p>
                    <p className="text-[10px] text-gray-400 mt-1">{b.desc}</p>
                  </div>
                ))}
              </div>
            </section>
          </>
        )}

        {/* Catalog */}
        <section id="catalog-section" className="max-w-6xl mx-auto px-6 py-12 scroll-mt-20">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
            <div className="text-left">
              <span className="text-xs font-bold uppercase tracking-widest text-[#4a5d4e]">ECO STOREFRONT</span>
              <h3 className="text-3xl font-bold font-serif text-[#2c3531] mt-1">Nectar Collections</h3>
            </div>

            {/* Filter controls */}
            <div className="flex items-center gap-2.5">
              <button 
                onClick={() => p.setFilterPanelOpen(true)}
                className="flex items-center gap-1.5 px-3.5 py-2 rounded-md border border-[#e1dcd1] bg-white hover:bg-gray-50 text-xs font-semibold text-gray-700 transition-colors"
              >
                <MdFilterList size={16} /> Filter
              </button>
              
              <div className="rounded-md border border-[#e1dcd1] bg-white p-1 flex gap-1">
                {[
                  ["products", getTabLabel(p, "products")],
                  ["services", getTabLabel(p, "services")],
                  ["details", getTabLabel(p, "details")]
                ].map(([k, l]) => (
                  <button
                    key={k}
                    onClick={() => {
                      p.setActiveTab(k);
                      if (k === "details") {
                        navigateTo("about");
                      } else {
                        navigateTo("shop");
                      }
                    }}
                    className="px-4 py-1.5 rounded-md text-xs font-semibold transition-all whitespace-nowrap"
                    style={p.activeTab === k ? { backgroundColor: primary, color: "#fff" } : { color: "#5c6b5e" }}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="w-full">
            <TabContent p={p} />
          </div>
        </section>
      </main>

      <StoreFooter p={p} />
    </div>
  );
}
